// Dashboard › Planner — staffing calculator (Alex, 2026-09-21/22).
//
// Answers the shift leader's question: "how many people does each job
// need, from now, to get the orders out by the dispatch cut-off?". One
// row per JOB — a department split by pick type where the speeds
// differ (Single / Pick & Sort / Large picking and packing; sorting is
// P&S only; shelf drop is an AM pile and a PM pile worked by one crew)
// — filled from GET /admin/dashboard/planner, polled every 30 s:
//
//   work    = what is still to do, by stage (orders due today, or every
//             open order — the toggle). Live, never typed.
//   rate    = the 7-day average per person per hour for that job
//             (editable for a what-if; the live figure is one click back)
//   on it   = the people Portal can see on that job right now
//
// The ONE control is the dispatch target (cut-off less a 15-minute
// buffer by default); the whole flow is worked back from it with
// handoff lags (helpers/plannerMath.ts), shelf drop keeps its fixed
// 08:30 / 13:00 finish-by times, and every time runs from the clock.
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { AlertTriangle, CheckCircle2, Clock, Compass, RefreshCw, RotateCcw, Users } from "lucide-react";
import { useTranslation } from "../i18n";
import { fetchPlanner, PlannerPayload, PlannerScope } from "../api/dashboardActivity";
import {
    DEFAULT_BUFFER_MINUTES, DEFAULT_DISPATCH_CUTOFF,
    PLANNER_DEPTS, PLANNER_ROWS, PlannerDeptKey, PlannerInput, PlannerRowKey, PlannerRowResult, PlannerRowSpec,
    clockMinutesOf, defaultTarget, formatClock, formatDuration, parseClock, plan,
} from "./helpers/plannerMath";
import DashboardPickAdvisor from "./DashboardPickAdvisor";

const STORAGE_KEY = "dashboard-planner:v4";
const POLL_MS = 30_000;

interface PlannerState {
    /** null = the default derived from the payload's cut-off. */
    target: string | null;
    scope: PlannerScope;
    /** Rates the leader typed over (what-ifs). */
    rateOverrides: Partial<Record<PlannerRowKey, number>>;
}

const DEFAULTS: PlannerState = { target: null, scope: "due", rateOverrides: {} };

const loadSaved = (): PlannerState => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return DEFAULTS;
        const saved = JSON.parse(raw) as Partial<PlannerState>;
        const rateOverrides: Partial<Record<PlannerRowKey, number>> = {};
        for (const r of PLANNER_ROWS) {
            const n = saved.rateOverrides?.[r.key];
            if (typeof n === "number" && n >= 0) rateOverrides[r.key] = n;
        }
        return {
            target: typeof saved.target === "string" && parseClock(saved.target) !== null ? saved.target : null,
            scope: saved.scope === "all" ? "all" : "due",
            rateOverrides,
        };
    } catch {
        return DEFAULTS;
    }
};

const save = (state: PlannerState) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch { /* storage full / private mode — the planner still works for this session */ }
};

interface LiveRow { work: number; rate: number | null; people: number; }
/** The payload's figures for one job: work still to do, 7-day rate, people on it now. */
const liveFor = (p: PlannerPayload, spec: PlannerRowSpec): LiveRow => {
    const t = spec.pickType as NonNullable<PlannerRowSpec["pickType"]>;
    switch (spec.dept) {
        case "shelf_drop":
            return {
                work: spec.shift === "am" ? p.work.shelf_drop.am_units : p.work.shelf_drop.pm_units,
                rate: p.rates.shelf_drop.units_ph,
                people: p.active.shelf_drop,
            };
        case "picking":
            return { work: p.work.picking[t].units, rate: p.rates.picking[t].units_ph, people: p.active.picking[t] };
        case "sorting":
            return {
                work: p.work.sorting.pick_and_sort.units,
                rate: p.rates.sorting.pick_and_sort.units_ph,
                people: p.active.sorting.pick_and_sort,
            };
        case "dispatch":
            return { work: p.work.dispatch[t].orders, rate: p.rates.dispatch[t].orders_ph, people: p.active.dispatch[t] };
        default:
            return { work: 0, rate: null, people: 0 };
    }
};

const fmtInt = (n: number) => Math.round(n).toLocaleString();
const fmtRate = (n: number) => (n >= 100 ? fmtInt(n) : (Math.round(n * 10) / 10).toLocaleString());
/** Numeric input value: blank for 0 so the placeholder shows, else the number. */
const numValue = (n: number) => (n === 0 ? "" : String(n));
const parseNum = (raw: string) => {
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
};
const formatClockIso = (iso: string) => {
    try {
        return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
        return "—";
    }
};
const formatIsoDate = (isoDate: string) => {
    const [y, m, d] = isoDate.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString([], { day: "numeric", month: "short" });
};

interface Props {
    warehouseFilter: string;
    /** Wall clock source — tests pin it. */
    clock?: () => number;
}

const PlannerStaffing = ({ warehouseFilter, clock = Date.now }: Props) => {
    const { t } = useTranslation();
    const [state, setState] = useState<PlannerState>(loadSaved);
    const [payload, setPayload] = useState<PlannerPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [nowMs, setNowMs] = useState(() => clock());
    const [reloadTick, setReloadTick] = useState(0);

    useEffect(() => { save(state); }, [state]);

    // Fetch on mount / warehouse / scope / Refresh, then every 30 s while
    // the tab is visible — "now" and the active headcounts both move.
    useEffect(() => {
        let alive = true;
        const tick = async () => {
            if (!alive || document.visibilityState === "hidden") return;
            setLoading(true);
            try {
                const p = await fetchPlanner(warehouseFilter, state.scope);
                if (!alive) return;
                setPayload(p);
                setFailed(false);
                setNowMs(clock());
            } catch {
                if (!alive) return;
                setFailed(true);
                toast.error(t("server_error"), { toastId: "dashboard-admin-load-err" });
            } finally {
                if (alive) setLoading(false);
            }
        };
        tick();
        const id = window.setInterval(tick, POLL_MS);
        const onVisible = () => { if (document.visibilityState === "visible") tick(); };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            alive = false;
            window.clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [warehouseFilter, state.scope, reloadTick, clock, t]);

    const cutoff = payload?.dispatch_cutoff && parseClock(payload.dispatch_cutoff) !== null
        ? payload.dispatch_cutoff
        : DEFAULT_DISPATCH_CUTOFF;
    const target = state.target ?? defaultTarget(cutoff);

    const live = useMemo(() => {
        const out = {} as Record<PlannerRowKey, LiveRow | null>;
        for (const r of PLANNER_ROWS) out[r.key] = payload ? liveFor(payload, r) : null;
        return out;
    }, [payload]);

    const input = useMemo<PlannerInput>(() => {
        const rows = {} as PlannerInput["rows"];
        for (const r of PLANNER_ROWS) {
            rows[r.key] = {
                work: live[r.key]?.work ?? 0,
                rate: state.rateOverrides[r.key] ?? live[r.key]?.rate ?? 0,
                people: live[r.key]?.people ?? 0,
            };
        }
        return { nowMinutes: clockMinutesOf(nowMs), target, rows };
    }, [state.rateOverrides, live, target, nowMs]);
    const result = useMemo(() => plan(input), [input]);

    // A crew's rows share one rate: editing either edits both.
    const crewMates = (key: PlannerRowKey) => {
        const spec = PLANNER_ROWS.find((r) => r.key === key) as PlannerRowSpec;
        return spec.crew === null ? [key] : PLANNER_ROWS.filter((r) => r.crew === spec.crew).map((r) => r.key);
    };
    const setRate = (key: PlannerRowKey, raw: string) =>
        setState((prev) => {
            const next = { ...prev.rateOverrides };
            for (const k of crewMates(key)) next[k] = parseNum(raw);
            return { ...prev, rateOverrides: next };
        });
    const clearRate = (key: PlannerRowKey) =>
        setState((prev) => {
            const next = { ...prev.rateOverrides };
            for (const k of crewMates(key)) delete next[k];
            return { ...prev, rateOverrides: next };
        });
    const reset = () => setState((prev) => ({ ...DEFAULTS, scope: prev.scope }));

    const statusBadge = (row: PlannerRowResult) => {
        switch (row.status) {
            case "nothing":
                return <span className="dash-badge dash-planner-badge--muted">{t("dashboard_planner_status_nothing")}</span>;
            case "no_rate":
                return <span className="dash-badge dash-planner-badge--muted">{t("dashboard_planner_status_no_rate")}</span>;
            case "no_time":
                return <span className="dash-badge dash-planner-badge--warn">{t("dashboard_planner_status_no_time")}</span>;
            case "nobody":
                return <span className="dash-badge dash-planner-badge--warn">{t("dashboard_planner_status_nobody")}</span>;
            case "on_track":
                return <span className="dash-badge dash-planner-badge--ok">{t("dashboard_planner_status_on_track")}</span>;
            case "late":
                return (
                    <span className="dash-badge dash-planner-badge--late">
                        {t("dashboard_planner_status_late", { time: formatDuration(row.lateMinutes ?? 0) })}
                    </span>
                );
            default:
                return null;
        }
    };

    const flow = (() => {
        switch (result.dispatchStatus) {
            case "nothing":
                return { tone: "muted", icon: <Users size={18} />, text: t("dashboard_planner_projected_none") };
            case "unknown":
                return { tone: "muted", icon: <Users size={18} />, text: t("dashboard_planner_projected_unknown") };
            case "blocked":
                return { tone: "late", icon: <AlertTriangle size={18} />, text: t("dashboard_planner_projected_blocked") };
            case "late":
                return {
                    tone: "late", icon: <AlertTriangle size={18} />,
                    text: t("dashboard_planner_projected_late", {
                        time: formatClock(result.dispatchFinishMinutes ?? 0),
                        late: formatDuration(result.dispatchLateMinutes),
                    }),
                };
            default:
                return {
                    tone: "ok", icon: <CheckCircle2 size={18} />,
                    text: t("dashboard_planner_projected_on_track", { time: formatClock(result.dispatchFinishMinutes ?? 0) }),
                };
        }
    })();

    const deptName = (dept: PlannerDeptKey) =>
        t(PLANNER_DEPTS.find((d) => d.key === dept)?.labelKey ?? dept);
    const jobName = (spec: PlannerRowSpec) =>
        spec.pickType
            ? t(`picking_pick_type_${spec.pickType}`)
            : spec.shift
                ? spec.shift.toUpperCase()
                : deptName(spec.dept);
    const rowLabel = (spec: PlannerRowSpec) =>
        spec.pickType
            ? `${deptName(spec.dept)} · ${t(`picking_pick_type_${spec.pickType}`)}`
            : spec.shift
                ? `${deptName(spec.dept)} · ${spec.shift.toUpperCase()}`
                : deptName(spec.dept);
    const finishLabel = (minutes: number | null) =>
        minutes === null ? null
            : !Number.isFinite(minutes) ? "—"
                : minutes >= 24 * 60 ? t("dashboard_planner_tomorrow")
                    : formatClock(minutes);

    const scopeButton = (scope: PlannerScope, labelKey: string) => (
        <button
            type="button"
            role="tab"
            aria-selected={state.scope === scope}
            className={`dash-scope-btn${state.scope === scope ? " dash-scope-btn--active" : ""}`}
            onClick={() => setState((p) => ({ ...p, scope }))}
        >
            {t(labelKey)}
        </button>
    );

    return (
        <div className="dash-planner">
            <p className="admin-settings-section-hint dash-planner-hint">{t("dashboard_planner_hint")}</p>

            <div className="dash-planner-controls">
                <div className="dash-planner-field">
                    <span>{t("dashboard_planner_scope_label")}</span>
                    <div className="dash-scope-toggle" role="tablist" aria-label={t("dashboard_planner_scope_label")}>
                        {scopeButton("due", "dashboard_planner_scope_due")}
                        {scopeButton("all", "dashboard_planner_scope_all")}
                    </div>
                </div>
                <label className="dash-planner-field">
                    <span>{t("dashboard_planner_target")}</span>
                    <input
                        type="time"
                        className="dash-pill-input"
                        value={target}
                        onChange={(e) => e.target.value && setState((p) => ({ ...p, target: e.target.value }))}
                        aria-label={t("dashboard_planner_target")}
                    />
                </label>
                <div className="dash-planner-actions">
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setReloadTick((n) => n + 1)}
                        disabled={loading}
                    >
                        <RefreshCw size={14} />
                        {loading ? t("loading") : t("dashboard_planner_refresh")}
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={reset} title={t("dashboard_planner_reset_hint")}>
                        <RotateCcw size={14} />
                        {t("dashboard_planner_reset")}
                    </button>
                </div>
            </div>

            {failed && <p className="dash-planner-error">{t("dashboard_planner_error")}</p>}

            <div className="dash-planner-tiles">
                <div className="dash-planner-tile">
                    <span className="dash-planner-tile-label"><Clock size={14} />{t("dashboard_planner_target")}</span>
                    <span className="dash-planner-tile-value" data-testid="planner-target">{target}</span>
                    <span className="dash-planner-tile-sub">
                        {t("dashboard_planner_target_sub", { cutoff, buffer: String(DEFAULT_BUFFER_MINUTES) })}
                    </span>
                </div>
                <div className="dash-planner-tile">
                    <span className="dash-planner-tile-label"><Clock size={14} />{t("dashboard_planner_time_left")}</span>
                    <span className="dash-planner-tile-value" data-testid="planner-time-left">
                        {result.minutesLeft > 0 ? formatDuration(result.minutesLeft) : t("dashboard_planner_past_target")}
                    </span>
                    <span className="dash-planner-tile-sub">
                        {t("dashboard_planner_now", { time: formatClock(input.nowMinutes) })}
                    </span>
                </div>
                <div className="dash-planner-tile">
                    <span className="dash-planner-tile-label"><Users size={14} />{t("dashboard_planner_people")}</span>
                    <span className="dash-planner-tile-value" data-testid="planner-people">
                        {t("dashboard_planner_people_summary", {
                            needed: result.complete && !result.empty ? String(result.totalNeeded) : "—",
                            active: String(result.totalPeople),
                        })}
                    </span>
                </div>
                <div className={`dash-planner-tile dash-planner-tile--${flow.tone}`}>
                    <span className="dash-planner-tile-label">{flow.icon}{t("dashboard_planner_projected")}</span>
                    <span className="dash-planner-tile-status" data-testid="planner-flow">{flow.text}</span>
                </div>
            </div>

            <table className="dash-table dash-planner-table">
                <thead>
                    <tr>
                        <th className="dash-planner-th-dept">{t("dashboard_planner_col_job")}</th>
                        <th className="dash-planner-th-needed">{t("dashboard_planner_col_work")}</th>
                        <th className="dash-planner-th-input">{t("dashboard_planner_col_rate")}</th>
                        <th className="dash-planner-th-needed">{t("dashboard_planner_col_needed")}</th>
                        <th className="dash-planner-th-people" title={t("dashboard_planner_active_hint")}>{t("dashboard_planner_col_active")}</th>
                        <th className="dash-planner-th-finish">{t("dashboard_planner_col_finish")}</th>
                    </tr>
                </thead>
                <tbody>
                    {PLANNER_DEPTS.map((dept) => {
                        const specs = PLANNER_ROWS.filter((r) => r.dept === dept.key);
                        // Jobs in one department can have different
                        // finish-by times (P&S picking feeds sorting first;
                        // shelf drop's AM and PM piles): the header carries
                        // the earliest still ahead, each row its own.
                        const deptRows = result.rows.filter((r) => r.dept === dept.key);
                        const ahead = deptRows.filter((r) => r.hoursAvailable > 0);
                        const pool = ahead.length > 0 ? ahead : deptRows;
                        const earliest = pool.reduce((a, r) => (r.deadlineMinutes < a.deadlineMinutes ? r : a), pool[0]);
                        return [
                            <tr key={`${dept.key}:head`} className="dash-planner-dept">
                                <td colSpan={6}>
                                    <div className="dash-planner-dept-bar">
                                        <span className="dash-planner-dept-name">{deptName(dept.key)}</span>
                                        <span className="dash-planner-dept-target" data-testid={`planner-by-${dept.key}`}>
                                            {t("dashboard_planner_dept_by", { time: formatClock(earliest.deadlineMinutes) })}
                                        </span>
                                        <span className="dash-planner-dept-hours" data-testid={`planner-hours-${dept.key}`}>
                                            {earliest.hoursAvailable > 0
                                                ? t("dashboard_planner_dept_hours", { time: formatDuration(earliest.hoursAvailable * 60) })
                                                : t("dashboard_planner_dept_passed")}
                                        </span>
                                    </div>
                                </td>
                            </tr>,
                            ...specs.map((spec) => {
                                const row = result.rows.find((r) => r.key === spec.key) as PlannerRowResult;
                                const inp = input.rows[spec.key];
                                const l = live[spec.key];
                                const unit = t(spec.unit === "orders" ? "dashboard_planner_unit_orders" : "dashboard_planner_unit_items");
                                const rateOver = state.rateOverrides[spec.key] !== undefined;
                                const finish = finishLabel(row.finishMinutes);
                                return (
                                    <tr key={spec.key} className={`dash-planner-row dash-planner-row--${row.status}`}>
                                        <td className="dash-td-primary dash-planner-job" data-label={t("dashboard_planner_col_job")}>
                                            {jobName(spec)}
                                        </td>
                                        <td data-label={t("dashboard_planner_col_work")}>
                                            <span className="dash-planner-needed" data-testid={`planner-work-${spec.key}`}>
                                                {payload ? fmtInt(inp.work) : "—"}
                                            </span>
                                            <span className="dash-planner-unit dash-planner-unit--after">{unit}</span>
                                        </td>
                                        <td data-label={t("dashboard_planner_col_rate")}>
                                            <div className="dash-planner-cell">
                                                <div className="dash-planner-input-wrap">
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step="any"
                                                        inputMode="decimal"
                                                        className={`admin-settings-po-input dash-planner-input${rateOver ? " dash-planner-input--edited" : ""}`}
                                                        value={numValue(inp.rate)}
                                                        placeholder={loading && !payload ? "…" : "0"}
                                                        onChange={(e) => setRate(spec.key, e.target.value)}
                                                        aria-label={`${rowLabel(spec)} — ${t("dashboard_planner_col_rate")}`}
                                                    />
                                                    <span className="dash-planner-unit">{unit}/hr</span>
                                                </div>
                                                {rateOver && (
                                                    <button
                                                        type="button"
                                                        className="dash-planner-revert"
                                                        onClick={() => clearRate(spec.key)}
                                                        title={t("dashboard_planner_revert_hint")}
                                                    >
                                                        {l?.rate === null || l?.rate === undefined
                                                            ? t("dashboard_planner_revert_none")
                                                            : t("dashboard_planner_revert", { n: fmtRate(l.rate) })}
                                                    </button>
                                                )}
                                                {!rateOver && l?.rate === null && payload && (
                                                    <span className="dash-planner-live">{t("dashboard_planner_no_history")}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td data-label={t("dashboard_planner_col_needed")}>
                                            <span className="dash-planner-needed" data-testid={`planner-needed-${spec.key}`}>
                                                {row.needed === null ? "—" : row.needed}
                                            </span>
                                            {row.shortfall !== null && row.shortfall > 0 && (
                                                <span className="dash-planner-shortfall">
                                                    {t("dashboard_planner_short", { n: String(row.shortfall) })}
                                                </span>
                                            )}
                                        </td>
                                        <td data-label={t("dashboard_planner_col_active")}>
                                            <span className="dash-planner-needed dash-planner-active" data-testid={`planner-active-${spec.key}`}>
                                                {payload ? inp.people : "—"}
                                            </span>
                                        </td>
                                        <td data-label={t("dashboard_planner_col_finish")}>
                                            <div className="dash-planner-finish">
                                                {finish !== null && (
                                                    <span className="dash-planner-finish-time" data-testid={`planner-finish-${spec.key}`}>
                                                        {finish}
                                                    </span>
                                                )}
                                                {statusBadge(row)}
                                                {row.status !== "nothing" && (
                                                    <span className="dash-planner-row-by" data-testid={`planner-rowby-${spec.key}`}>
                                                        {t("dashboard_planner_row_by", { time: formatClock(row.deadlineMinutes) })}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }),
                        ];
                    })}
                </tbody>
            </table>

            {payload && (
                <p className="dash-planner-notes" data-testid="planner-footer">
                    {t("dashboard_planner_footer", {
                        time: formatClockIso(payload.snapshot_at ?? payload.generated_at),
                        scope: t(payload.scope === "all" ? "dashboard_planner_scope_all" : "dashboard_planner_scope_due"),
                        days: String(payload.rates_window.days),
                        from: formatIsoDate(payload.rates_window.from),
                        to: formatIsoDate(payload.rates_window.to),
                        n: String(payload.work.flagged.orders),
                    })}
                </p>
            )}
        </div>
    );
};

// The Planner has two views (Alex, 2026-10-07): the staffing
// calculator above, and the Pick Advisor — what the queue is telling a
// free picker right now and why (DashboardPickAdvisor). The choice is
// remembered per device; only the open view polls.
type PlannerView = "staffing" | "advisor";
const VIEW_KEY = "dashboard-planner:view";
const loadView = (): PlannerView => {
    try {
        return window.localStorage.getItem(VIEW_KEY) === "advisor" ? "advisor" : "staffing";
    } catch {
        return "staffing";
    }
};

const DashboardPlanner = ({ warehouseFilter, clock }: Props) => {
    const { t } = useTranslation();
    const [view, setView] = useState<PlannerView>(loadView);
    useEffect(() => {
        try { window.localStorage.setItem(VIEW_KEY, view); } catch { /* per-device convenience only */ }
    }, [view]);
    const tab = (v: PlannerView, labelKey: string, icon: JSX.Element) => (
        <button
            type="button"
            role="tab"
            aria-selected={view === v}
            className={`dash-scope-btn${view === v ? " dash-scope-btn--active" : ""}`}
            onClick={() => setView(v)}
        >
            {icon}
            {t(labelKey)}
        </button>
    );
    return (
        <div className="dash-planner-shell">
            <div className="dash-scope-toggle dash-planner-views" role="tablist" aria-label={t("dashboard_planner_views")}>
                {tab("staffing", "dashboard_planner_view_staffing", <Users size={14} />)}
                {tab("advisor", "dashboard_planner_view_advisor", <Compass size={14} />)}
            </div>
            {view === "staffing"
                ? <PlannerStaffing warehouseFilter={warehouseFilter} clock={clock} />
                : <DashboardPickAdvisor warehouseFilter={warehouseFilter} clock={clock} />}
        </div>
    );
};

export default DashboardPlanner;
